import dayjs from 'dayjs';
import useAthleteEvents from 'hooks/athleteEvent/useAthleteEvents';
import { AthleteEvent } from 'types';

interface ReservationsSummary {
  upcomingReservations: AthleteEvent[];
  hasUpcomingReservations: boolean;
  upcomingCreditedCount: number;
  upcomingUncreditedCount: number;
  upcomingTotalCount: number;
  isLoading: boolean;
}

export default function useUpcomingReservationsSummary(
  athletePackageId: string,
  cancellationDate: Date | null,
  cancelNow: boolean,
): ReservationsSummary {
  const targetDate = cancelNow ? new Date() : cancellationDate;
  const enabled = !!targetDate;
  const startDateRange = targetDate ? dayjs(targetDate).format('YYYY-MM-DD') : undefined;

  const { data, isLoading } = useAthleteEvents({
    athletePackage: athletePackageId,
    // Cancelling reaches every future reservation, so staff choose over all of them.
    includeUncredited: true,
    type: 'upcoming',
    startDateRange,
    limit: '100',
    offset: '0',
    ordering: 'startDate',
    enabled,
  });

  // Cancelling leaves a late cancel or a no show alone, so the list must not offer them.
  // Mirrors the filter in AthletePackage.objects.cancel().
  const upcomingReservations: AthleteEvent[] = (data?.pages.flatMap(page => page.data.results) ?? []).filter(
    event => !event.isLateCancel && !event.isNoShow,
  );

  const upcomingCreditedCount = upcomingReservations.filter(e => e.isCredited).length;
  const upcomingUncreditedCount = upcomingReservations.filter(e => !e.isCredited).length;
  const upcomingTotalCount = upcomingReservations.length;

  return {
    upcomingReservations,
    hasUpcomingReservations: upcomingTotalCount > 0,
    upcomingCreditedCount,
    upcomingUncreditedCount,
    upcomingTotalCount,
    isLoading: enabled && isLoading,
  };
}
